import { authApi } from '@/api/auth';
import { isLanguageSwitcherEnabled } from '@/lib/i18n-config';
import { useAuthStore } from '@/stores/auth-store';
import { useSidebarStore } from '@/stores/sidebar-store';
import { useThemeStore } from '@/stores/theme-store';
import {
  LOCALE_CONFIGS,
  SUPPORTED_LOCALES,
  type SupportedLocale,
  useLocale,
  useTranslation,
} from '@kgc/i18n';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const LOCALE_FLAGS: Record<SupportedLocale, string> = {
  hu: '🇭🇺',
  en: '🇬🇧',
  de: '🇩🇪',
  uk: '🇺🇦',
};

interface HeaderProps {
  onCommandPaletteOpen?: () => void;
  pendingSyncCount?: number;
}

export function Header({ onCommandPaletteOpen, pendingSyncCount = 0 }: HeaderProps) {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useThemeStore();
  const { user, logout } = useAuthStore();
  const { locale, setLocale } = useLocale();
  const { t } = useTranslation();
  const { openOverlay } = useSidebarStore();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const isDark = theme === 'dark';

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore logout API errors
    }
    logout();
    navigate('/login');
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const adminLinks = [
    {
      labelKey: 'nav.users',
      href: '/users',
      icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
    },
    {
      labelKey: 'nav.permissions',
      href: '/users/roles',
      icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
    },
    {
      labelKey: 'nav.tenantAdmin',
      href: '/tenant',
      icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
    },
    {
      labelKey: 'nav.featureFlags',
      href: '/feature-flags',
      icon: 'M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9',
    },
    {
      labelKey: 'nav.settings',
      href: '/settings',
      icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
    },
  ];

  return (
    <header className="relative z-50 flex md:h-9 h-11 shrink-0 items-center justify-between border-b bg-card px-4">
      {/* Left side */}
      <div className="flex items-center gap-2">
        {/* Hamburger — lg alatt (sidebar hidden tablet+mobil) */}
        <button
          className="lg:hidden flex h-7 w-7 items-center justify-center rounded-md border bg-background text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label={t('nav.expand')}
          onClick={openOverlay}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
        {pendingSyncCount > 0 && (
          <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
            <svg
              className="h-3 w-3 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 12a9 9 0 11-6.219-8.56" />
            </svg>
            {pendingSyncCount} {t('header.pendingSync') || 'szinkronizálandó'}
          </span>
        )}
      </div>

      {/* Right side - Search, Theme toggle, Admin dropdown & User */}
      <div className="flex shrink-0 items-center gap-2">
        {/* Search button (Ctrl+K) */}
        <button
          onClick={onCommandPaletteOpen}
          className="flex h-7 items-center gap-1.5 rounded-md border bg-background px-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          title={t('header.searchShortcut')}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <span className="hidden text-sm sm:inline">{t('header.search')}</span>
          <kbd className="hidden rounded bg-muted px-1.5 py-0.5 text-xs font-medium sm:inline">
            Ctrl+K
          </kbd>
        </button>

        {/* Theme toggle button */}
        <button
          onClick={toggleTheme}
          className="flex h-7 w-7 items-center justify-center rounded-md border bg-background text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          title={isDark ? t('header.lightMode') : t('header.darkMode')}
        >
          {isDark ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
              />
            </svg>
          )}
        </button>

        {/* Language switcher — demo-trust: rejtve amíg a nem-HU fordítások
            inkomplettek (VITE_I18N_SWITCHER_ENABLED opt-in) */}
        {isLanguageSwitcherEnabled() && (
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex h-7 items-center gap-1 rounded-md border bg-background px-2 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              title={t('header.language')}
            >
              <span>{LOCALE_FLAGS[locale]}</span>
              <span className="hidden text-xs font-medium uppercase sm:inline">{locale}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-3 w-3 transition-transform ${langOpen ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {langOpen && (
              <div className="absolute right-0 top-full z-[60] mt-2 w-44 rounded-lg border border-border/50 bg-card/80 shadow-xl backdrop-blur-md">
                <div className="py-1">
                  {SUPPORTED_LOCALES.map(loc => (
                    <button
                      key={loc}
                      onClick={() => {
                        setLocale(loc);
                        setLangOpen(false);
                      }}
                      className={`flex w-full items-center gap-3 px-4 py-2 text-sm transition-colors hover:bg-muted ${
                        locale === loc
                          ? 'bg-muted/50 font-medium text-foreground'
                          : 'text-muted-foreground'
                      }`}
                    >
                      <span className="text-base">{LOCALE_FLAGS[loc]}</span>
                      <span>{LOCALE_CONFIGS[loc].nativeName}</span>
                      {locale === loc && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="ml-auto h-4 w-4 text-primary"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* User dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-1.5 rounded-md border bg-background px-2 py-1 text-xs transition-colors hover:bg-muted"
          >
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-white">
              {user?.name?.charAt(0) || 'U'}
            </div>
            <span className="hidden text-foreground sm:inline">
              {user?.name || t('header.user')}
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-4 w-4 text-muted-foreground transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {/* Dropdown menu */}
          {dropdownOpen && (
            <div className="absolute right-0 top-full z-[60] mt-2 w-56 rounded-lg border border-border/50 bg-card/80 shadow-xl backdrop-blur-md">
              {/* User info */}
              <div className="border-b px-4 py-3">
                <p className="text-sm font-medium text-foreground">
                  {user?.name || t('header.user')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {user?.email || 'email@example.com'}
                </p>
                <p className="mt-1 text-xs text-primary">{user?.role || t('header.role')}</p>
              </div>

              {/* Admin links */}
              <div className="border-b py-2">
                <p className="px-4 py-1 text-xs font-medium uppercase text-muted-foreground">
                  {t('nav.admin')}
                </p>
                {adminLinks.map(link => (
                  <button
                    key={link.href}
                    onClick={() => {
                      navigate(link.href);
                      setDropdownOpen(false);
                    }}
                    className="flex w-full items-center gap-3 px-4 py-2 text-sm text-foreground transition-colors hover:bg-muted"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-muted-foreground"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d={link.icon}
                      />
                    </svg>
                    {t(link.labelKey)}
                  </button>
                ))}
              </div>

              {/* Logout */}
              <div className="py-2">
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center gap-3 px-4 py-2 text-sm text-red-500 transition-colors hover:bg-red-50 dark:hover:bg-red-950/20"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  {t('header.logout')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
